import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MapThumb, MapViewer } from "@/components/PassageMaps";

const PAGE = 24;

export const MapBrowser = ({ book = "", place = "", testid = "map-browser" }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const reqId = useRef(0);

  const fetchPage = (skip) => {
    const id = ++reqId.current;
    setLoading(true);
    return api.get("/maps/browse", { params: { book, place, skip, limit: PAGE } })
      .then(({ data }) => {
        if (id !== reqId.current) return;
        setTotal(data.total);
        setItems((prev) => (skip === 0 ? data.items : [...prev, ...data.items]));
      })
      .catch(() => {})
      .finally(() => { if (id === reqId.current) setLoading(false); });
  };

  useEffect(() => {
    setItems([]);
    if (book || place) fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book, place]);

  if (!book && !place) return null;

  return (
    <div data-testid={testid}>
      <p data-testid={`${testid}-total`} className="font-mono text-xs uppercase tracking-widest text-ink/50 mb-4">
        {loading && !items.length ? "Searching the atlas…" : `${total} map${total === 1 ? "" : "s"}`}
      </p>
      {!loading && total === 0 && (
        <p data-testid={`${testid}-empty`} className="text-ink/60 text-sm">No maps found. Try another spelling or a nearby place.</p>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m) => <MapThumb key={m.id} m={m} onOpen={setOpen} />)}
      </div>
      {items.length < total && (
        <div className="text-center mt-8">
          <Button data-testid={`${testid}-load-more`} disabled={loading} onClick={() => fetchPage(items.length)}
            className="rounded-full bg-ink text-parchment hover:bg-gold hover:text-ink px-8 h-12 font-700">
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Show more maps
          </Button>
        </div>
      )}
      <MapViewer map={open} onClose={() => setOpen(null)} />
    </div>
  );
};
